import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { 
  Brain, Bot, ChevronDown, X, Search, 
  Sparkles, Code, Target, BookOpen, Beaker
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { Agent } from "@/types/aiToolkit";
import { AGENTS } from "@/data/aiToolkit";
import { agentMatchesIntegrationScope } from "@/data/communicationDefaultAgents";

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  Brain,
  Bot,
  Sparkles,
  Code,
  Target,
  BookOpen,
  Beaker,
};

interface UnifiedAgent {
  id: string;
  name: string;
  description: string;
  specialty?: string;
  icon: string;
  color: string;
  isSystem: boolean;
  capabilityModules?: string[];
  integrationScopes?: string[];
}

interface AgentSelectorProps {
  value: string | null;
  onValueChange: (agentId: string | null) => void;
  compact?: boolean;
  isActive?: boolean;
  disabled?: boolean;
  /** When set, only agents scoped to this integration (or `cross`) are shown. */
  integrationScope?: string;
}

export function AgentSelector({ 
  value, 
  onValueChange, 
  compact = false,
  isActive = true,
  disabled = false,
  integrationScope,
}: AgentSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: userAgents = [] } = useQuery<Agent[]>({
    queryKey: ["/api/ai-agents"],
    queryFn: async () => {
      const { aiAgentsService } = await import("@/services/aiAgentsService");
      return aiAgentsService.getAgents();
    },
    enabled: isActive,
  });

  const unifiedAgents: UnifiedAgent[] = [
    ...userAgents.map((a) => ({
      id: a.id,
      name: a.name,
      description: a.description,
      specialty: a.description,
      icon: a.icon || "Bot",
      color: a.color || "bg-purple-500",
      isSystem: false,
      capabilityModules: a.capabilityModules,
      integrationScopes: a.integrationScopes,
    })),
    ...AGENTS.map((a: Agent) => ({
      id: a.id,
      name: a.name,
      description: a.description,
      specialty: a.description,
      icon: a.icon,
      color: a.color,
      isSystem: true,
      capabilityModules: a.capabilityModules,
      integrationScopes: a.integrationScopes,
    })),
  ];

  const scopedAgents = integrationScope
    ? unifiedAgents.filter((agent) =>
        agentMatchesIntegrationScope(agent.integrationScopes, integrationScope)
      )
    : unifiedAgents;

  const filteredAgents = scopedAgents.filter((agent) => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return (
        agent.name.toLowerCase().includes(query) ||
        agent.description.toLowerCase().includes(query) ||
        (agent.specialty && agent.specialty.toLowerCase().includes(query))
      );
    }
    return true;
  });

  const selectedAgent = value 
    ? scopedAgents.find(a => a.id === value) ?? unifiedAgents.find(a => a.id === value)
    : null;

  const handleSelect = (agent: UnifiedAgent) => {
    onValueChange(agent.id);
    setOpen(false);
    setSearchQuery("");
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onValueChange(null);
  };

  const SelectedIcon = selectedAgent 
    ? ICON_MAP[selectedAgent.icon] || Bot 
    : null;

  return (
    <Popover open={open} onOpenChange={(next) => !disabled && setOpen(next)}>
      <PopoverTrigger asChild>
        <Button 
          variant={selectedAgent ? "default" : "outline"} 
          size={compact ? "sm" : "default"}
          disabled={disabled}
          className={cn(
            "gap-2 justify-between w-full",
            selectedAgent && selectedAgent.color,
            compact ? "h-8 px-2" : "min-w-[180px]"
          )}
          data-testid="select-agent"
        >
          {selectedAgent ? (
            <>
              {SelectedIcon && <SelectedIcon className="h-4 w-4 text-white" />}
              <span className="truncate max-w-[120px] text-white">{selectedAgent.name}</span>
              <X 
                className="h-3 w-3 ml-1 hover:bg-white/20 rounded text-white" 
                onClick={handleClear}
              />
            </>
          ) : (
            <>
              <Bot className="h-4 w-4" />
              <span>AI Agent</span>
              <ChevronDown className="h-3 w-3 ml-1" />
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search agents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-9"
              data-testid="input-search-agents"
            />
          </div>
        </div>

        <ScrollArea className="h-[280px]">
          <div className="p-2 space-y-1">
            {selectedAgent && (
              <Button
                variant="ghost"
                className="w-full justify-start gap-2 h-auto py-2 text-muted-foreground"
                onClick={() => {
                  onValueChange(null);
                  setOpen(false);
                  setSearchQuery("");
                }}
              >
                <X className="h-4 w-4" />
                Clear selection
              </Button>
            )}
            
            {filteredAgents.map((agent) => {
              const AgentIcon = ICON_MAP[agent.icon] || Bot;
              const isSelected = value === agent.id;
              
              return (
                <Button
                  key={agent.id}
                  variant={isSelected ? "secondary" : "ghost"}
                  className={cn(
                    "w-full justify-start gap-2 h-auto py-2 px-2",
                    isSelected && "bg-secondary"
                  )}
                  onClick={() => handleSelect(agent)}
                  data-testid={`button-agent-${agent.id}`}
                >
                  <div className={cn(
                    "w-8 h-8 rounded-md flex items-center justify-center text-white shrink-0",
                    agent.color
                  )}>
                    <AgentIcon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">{agent.name}</span>
                      {agent.isSystem && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0 shrink-0">
                          Built-in
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{agent.description}</p>
                    {agent.capabilityModules && agent.capabilityModules.length > 0 && (
                      <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                        {agent.capabilityModules.slice(0, 2).map((cap) => (
                          <Badge key={cap} variant="outline" className="text-[10px] px-1 py-0">
                            {cap.replace('cap-', '')}
                          </Badge>
                        ))}
                        {agent.capabilityModules.length > 2 && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0">
                            +{agent.capabilityModules.length - 2}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </Button>
              );
            })}

            {filteredAgents.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Bot className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No agents found</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
